import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { validatePhone } from '@nxsys/utils/validator';
import { Form } from 'antd';
import React, { Ref, useImperativeHandle } from 'react';

export type AgencyFormValues = {
  contactName?: string;
  email?: string;
  name?: string;
  phone?: any;
};

export type AgencyFormRef = {
  submit: () => void;
  reset: () => void;
};

type AgencyFormProps = {
  formRef?: Ref<AgencyFormRef>;
  onSubmit?: (values: AgencyFormValues) => void;
  defaultValues?: AgencyFormValues;
  isEditing?: boolean;
};

const AgencyForm: React.FC<AgencyFormProps> = ({
  formRef,
  onSubmit,
  defaultValues,
  isEditing = false,
}) => {
  const { messages } = useTypedIntl();

  const [form] = Form.useForm<AgencyFormValues>();

  useImperativeHandle(formRef, () => ({
    submit() {
      form.submit();
    },
    reset() {
      form.resetFields();
    },
  }));

  const formItems: { label: string; name: keyof AgencyFormValues; required?: boolean; type: string; placeholder: string; rules?: any[] }[] = [
    {
      label: 'Agency Name',
      name: 'name',
      required: true,
      type: 'text',
      placeholder: messages['form.enter'],
    },
    {
      label: 'Contact Name',
      name: 'contactName',
      required: isEditing,
      type: 'text',
      placeholder: messages['form.enter'],
    },
    {
      label: messages['common.email'],
      name: 'email',
      required: !isEditing,
      type: 'text',
      placeholder: messages['form.enterEmail'],
      rules: [
        {
          type: 'email' as const,
          message: messages['form.enterValidEmail'],
        },
      ],
    },
    {
      label: messages['common.phoneNumber'],
      name: 'phone',
      type: 'phone',
      placeholder: messages['form.enterPhoneNumber'],
      rules: [
        {
          validator: (_: any, value: string) => {
            if (!value) return Promise.resolve();
            if (!validatePhone(value)) {
              return Promise.reject(messages['form.enterValidMobile']);
            }
            return Promise.resolve();
          },
        },
      ],
    },
  ];

  const onFinish = (values: AgencyFormValues) => {
    onSubmit?.(values);
  };

  const onFinishFailed = (errorInfo: any) => {
    console.error(errorInfo);
  };

  return (
    <AppFormContainer
      form={form}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      {...(defaultValues ? { initialValues: defaultValues } : {})}
    >
      {formItems.map((item) => (
        <Form.Item
          key={item.name}
          label={item.label}
          name={item.name}
          rules={[
            {
              required: !!item.required,
              message: messages['form.pleaseEnter'],
            },
            ...(item.rules ?? []),
          ]}
        >
          {item.type === 'phone' ? (
            <AppInput
              placeholder={item.placeholder}
              onChange={(e) => {
                form.setFieldValue(
                  item.name,
                  e.target.value.replace(/\D/g, ''),
                );
              }}
            />
          ) : item.type === 'text' ? (
            <AppInput
              placeholder={item.placeholder}
              type={item.type}
              allowCapitalize={item.name !== 'email'}
            />
          ) : null}
        </Form.Item>
      ))}
    </AppFormContainer>
  );
};

export default AgencyForm;
